/**
 * Utility functions for parsing generated content from the API
 * ALL PARSING LOGIC IS HERE - SIMPLE AND CLEAN
 * 
 * Logging is delegated to contentParserLogger.ts for easier control
 */
import * as logger from './contentParserLogger';

export interface ParsedVariation {
  content: string;
  metadata: any | null;
}

export interface ParsedPlatformContent {
  context?: string;
  variations: Array<{ key: string; data: any; index: number }>;
}

// Type definitions for the JSON structure
interface VariationData {
  content: string;
  metadata?: {
    character_count?: number;
    hashtags?: string[];
    call_to_action?: string;
    tone?: string;
    optimization_notes?: string;
  };
}

interface PlatformData {
  variation_1?: VariationData;
  variation_2?: VariationData;
  context?: string;
}

interface GeneratedContentStructure {
  facebook?: PlatformData;
  linkedin?: PlatformData;
  instagram?: PlatformData;
  twitter?: PlatformData;
  youtube?: PlatformData;
  pinterest?: PlatformData;
  email?: PlatformData;
  blog?: PlatformData;
  tiktok?: PlatformData;
}

/**
 * Find and extract the complete JSON object from a string
 */
const extractJSONObject = (str: string): string | null => {
  const firstBrace = str.indexOf('{');
  if (firstBrace === -1) return null;
  
  let depth = 0;
  let inString = false;
  let escapeNext = false;
  
  for (let i = firstBrace; i < str.length; i++) {
    const char = str[i];
    
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    
    if (char === '\\') {
      escapeNext = true;
      continue;
    }
    
    if (char === '"' && !escapeNext) {
      inString = !inString;
      continue;
    }
    
    if (!inString) {
      if (char === '{') depth++;
      else if (char === '}') {
        depth--;
        if (depth === 0) {
          return str.substring(firstBrace, i + 1);
        }
      }
    }
  }
  
  return null;
};

/**
 * Escape newlines ONLY inside string values, not structural newlines
 * This properly handles JSON with literal newlines in content strings
 */
const escapeNewlinesInStrings = (jsonString: string): string => {
  let result = '';
  let inString = false;
  let escapeNext = false;
  
  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString[i];
    
    if (escapeNext) {
      // Previous char was backslash, this char is escaped
      result += char;
      escapeNext = false;
      continue;
    }
    
    if (char === '\\') {
      // Next char will be escaped
      result += char;
      escapeNext = true;
      continue;
    }
    
    if (char === '"') {
      // Toggle string state
      inString = !inString;
      result += char;
      continue;
    }
    
    if (inString) {
      // Inside a string - escape newlines and control characters
      if (char === '\n') {
        result += '\\n';
      } else if (char === '\r') {
        result += '\\r';
      } else if (char === '\t') {
        result += '\\t';
      } else if (char >= '\x00' && char <= '\x1F' && char !== '\n' && char !== '\r' && char !== '\t') {
        // Remove other control characters
        continue;
      } else {
        result += char;
      }
    } else {
      // Outside a string - keep as-is (including structural newlines)
      result += char;
    }
  }
  
  return result;
};

/**
 * Parse JSON safely - handles control characters
 */
const parseJSONSafely = (jsonString: string): any | null => {
  logger.logParseJSONSafelyStart(jsonString.length, jsonString.substring(0, 300));
  
  // Try parsing as-is first
  try {
    const parsed = JSON.parse(jsonString);
    logger.logParseJSONSafelySuccess();
    return parsed;
  } catch (directError: any) {
    logger.logParseJSONSafelyFailed(directError);
    
    // If it's a control character error, escape newlines ONLY in strings
    if (directError.message && directError.message.includes('control character')) {
      try {
        // Escape newlines only inside string values
        const escaped = escapeNewlinesInStrings(jsonString);
        logger.logParseJSONSafelyEscaped(escaped.substring(0, 300));
        
        const parsed = JSON.parse(escaped);
        logger.logParseJSONSafelyEscapedSuccess();
        return parsed;
      } catch (escapeError: any) {
        const position = escapeError.message.match(/position (\d+)/)?.[1];
        logger.logParseJSONSafelyEscapedFailed(escapeError, position);
        return null;
      }
    }
    
    return null;
  }
};

/**
 * Validate and parse the generated content structure
 */
const parseGeneratedContentStructure = (data: any): GeneratedContentStructure | null => {
  logger.logParseGeneratedContentStructureStart(data);
  
  if (typeof data !== 'object' || data === null) {
    logger.logParseGeneratedContentStructureNotObject();
    return null;
  }
  
  // Check if it has platform keys
  const platformKeys = ['facebook', 'linkedin', 'instagram', 'twitter', 'youtube', 'pinterest', 'email', 'blog', 'tiktok'];
  const hasPlatformKeys = Object.keys(data).some(key => platformKeys.includes(key.toLowerCase()));
  
  if (hasPlatformKeys) {
    logger.logParseGeneratedContentStructureHasPlatformKeys();
    return data as GeneratedContentStructure;
  }
  
  logger.logParseGeneratedContentStructureNoPlatformKeys();
  return null;
};

/**
 * Extract generated content from API response
 */
export const extractGeneratedContent = (generatedContent: any): { content: any; context?: string } => {
  logger.logExtractGeneratedContentStart(generatedContent);
  
  let content = generatedContent?.generated_content || generatedContent;
  let context: string | undefined;
  
  // Check for context in generation_metadata
  if (generatedContent?.generation_metadata) {
    const metadata = generatedContent.generation_metadata;
    if (typeof metadata === 'object' && metadata !== null) {
      context = metadata.context || metadata.description || metadata.explanation || metadata.summary;
    } else if (typeof metadata === 'string') {
      try {
        const parsedMeta = JSON.parse(metadata);
        context = parsedMeta.context || parsedMeta.description || parsedMeta.explanation || parsedMeta.summary;
      } catch (e) {
        context = metadata;
      }
    }
  }
  
  // Parse JSON string if needed
  if (typeof content === 'string') {
    logger.logExtractGeneratedContentString(content.length, content.substring(0, 500));
    
    // Extract JSON from string (handles text before JSON)
    const extracted = extractJSONObject(content);
    
    if (extracted) {
      // Extract context from text before JSON
      const firstBrace = content.indexOf('{');
      if (firstBrace > 0) {
        const textBefore = content.substring(0, firstBrace).trim();
        if (textBefore.length > 50 && !context) {
          context = textBefore;
        }
      }
      
      // Parse the extracted JSON
      const parsed = parseJSONSafely(extracted);
      if (parsed) {
        content = parsed;
      }
    } else {
      // Try parsing the whole string
      const parsed = parseJSONSafely(content);
      if (parsed) {
        content = parsed;
      }
    }
  }
  
  // Validate the structure
  const validated = parseGeneratedContentStructure(content);
  if (validated) {
    content = validated;
  }
  
  logger.logExtractGeneratedContentEnd(content);
  
  if (context) {
    return { content, context };
  }
  
  return { content };
};

/**
 * Parse a variation object
 */
export const parseVariation = (variation: any, platformKey?: string, variationKey?: string): ParsedVariation => {
  logger.logParseVariationStart(platformKey, variationKey, typeof variation);
  logger.logParseVariationInput(variation);
  
  // If it's an object with content and metadata
  if (typeof variation === 'object' && variation !== null) {
    // Check if it's a variation object
    if ('content' in variation && typeof variation.content === 'string') {
      logger.logParseVariationFoundObject();
      
      // If content is a string that might contain JSON with markdown code blocks, try to parse it
      const contentStr = variation.content;
      if (contentStr.includes('```') || (contentStr.trim().startsWith('{') && contentStr.length > 100)) {
        logger.logParseVariationJSONString();
        const parsed = parseVariationContent(contentStr, platformKey || '', variationKey || '');
        if (parsed) {
          logger.logParseVariationNestedSuccess();
          return parsed;
        }
      }
      
      return {
        content: variation.content,
        metadata: variation.metadata || null,
      };
    }
    
    // Check if it has platform keys (entire content structure)
    const platformKeys = ['facebook', 'linkedin', 'instagram', 'twitter', 'youtube', 'pinterest', 'email', 'blog', 'tiktok'];
    const hasPlatformKeys = Object.keys(variation).some(key => platformKeys.includes(key.toLowerCase()));
    
    if (hasPlatformKeys && platformKey && variation[platformKey]) {
      const platformData = variation[platformKey];
      if (variationKey && platformData[variationKey]) {
        const specificVariation = platformData[variationKey];
        if (typeof specificVariation === 'object' && 'content' in specificVariation) {
          logger.logParseVariationExtractedFromContent();
          return {
            content: typeof specificVariation.content === 'string' ? specificVariation.content : String(specificVariation.content || ''),
            metadata: specificVariation.metadata || null,
          };
        }
      }
    }
    
    // Check if it has variation keys (platform structure)
    if (variationKey && variationKey in variation) {
      const specificVariation = variation[variationKey];
      if (typeof specificVariation === 'object' && 'content' in specificVariation) {
        logger.logParseVariationExtractedFromPlatform();
        return {
          content: typeof specificVariation.content === 'string' ? specificVariation.content : String(specificVariation.content || ''),
          metadata: specificVariation.metadata || null,
        };
      }
    }
    
    // Try alternative keys
    const contentValue = variation.content || variation.text || variation.body || '';
    const metadataValue = variation.metadata || variation.meta || null;
    
    return {
      content: typeof contentValue === 'string' ? contentValue : '',
      metadata: metadataValue,
    };
  }
  
  // If it's a string, try to parse it
  if (typeof variation === 'string') {
    if (variation.trim().startsWith('{')) {
      const extracted = extractJSONObject(variation);
      if (extracted) {
        const parsed = parseJSONSafely(extracted);
        if (parsed) {
          // Recursively parse
          return parseVariation(parsed, platformKey, variationKey);
        }
      }
    }
    return { content: variation, metadata: null };
  }
  
  return { content: '', metadata: null };
};

/**
 * Parse platform content
 */
export const parsePlatformContent = (platformContent: any, platformKey?: string): ParsedPlatformContent => {
  logger.logParsePlatformContentStart(platformKey, typeof platformContent);
  logger.logParsePlatformContentInput(platformContent);
  
  // If it's a string, parse it first
  if (typeof platformContent === 'string') {
    const extracted = extractJSONObject(platformContent);
    if (extracted) {
      const parsed = parseJSONSafely(extracted);
      if (parsed) {
        // Check if parsed has the platform key
        if (platformKey && typeof parsed === 'object' && parsed !== null && platformKey in parsed) {
          platformContent = parsed[platformKey];
        } else if (typeof parsed === 'object' && parsed !== null) {
          // Check if it has variation keys directly
          const hasVariations = Object.keys(parsed).some(key => key.startsWith('variation_'));
          if (hasVariations) {
            platformContent = parsed;
          } else {
            platformContent = parsed;
          }
        }
      }
    } else {
      const parsed = parseJSONSafely(platformContent);
      if (parsed) {
        if (platformKey && typeof parsed === 'object' && parsed !== null && platformKey in parsed) {
          platformContent = parsed[platformKey];
        } else {
          platformContent = parsed;
        }
      }
    }
  }
  
  const result: ParsedPlatformContent = {
    context: undefined,
    variations: [],
  };
  
  if (typeof platformContent === 'object' && platformContent !== null) {
    // Check for context
    if ('context' in platformContent) {
      result.context = typeof platformContent.context === 'string' 
        ? platformContent.context 
        : String(platformContent.context || '');
    }
    
    // First, check for variation_1, variation_2, etc. at top level (normal case)
    // This is the expected structure when data is correctly parsed
    const topLevelVariations = Object.keys(platformContent).filter(key => key.startsWith('variation_'));
    
    // SPECIAL CASE: If variation_1 exists and its content is a JSON string containing platform data,
    // we need to parse it to extract all variations (variation_1 AND variation_2)
    if (topLevelVariations.length > 0 && 
        'variation_1' in platformContent &&
        platformContent.variation_1 &&
        typeof platformContent.variation_1 === 'object' &&
        'content' in platformContent.variation_1 &&
        typeof platformContent.variation_1.content === 'string' &&
        (platformContent.variation_1.content.trim().startsWith('{') || 
         platformContent.variation_1.content.includes('```json'))) {
      
      logger.logParsePlatformContentNestedJSON();
      
      // Parse the JSON string in variation_1.content
      let contentToParse = platformContent.variation_1.content;
      
      // Strip markdown code blocks if present
      if (contentToParse.includes('```')) {
        const stripMarkdownCodeBlock = (str: string): string => {
          let cleaned = str.trim();
          if (cleaned.startsWith('```')) {
            const firstNewline = cleaned.indexOf('\n');
            if (firstNewline !== -1) {
              cleaned = cleaned.substring(firstNewline + 1);
            } else {
              cleaned = cleaned.substring(3);
            }
          }
          if (cleaned.endsWith('```')) {
            cleaned = cleaned.substring(0, cleaned.length - 3).trim();
          }
          return cleaned.trim();
        };
        contentToParse = stripMarkdownCodeBlock(contentToParse);
      }
      
      const extracted = extractJSONObject(contentToParse);
      if (extracted) {
        logger.logParsePlatformContentExtractedLength(extracted.length);
        const parsed = parseJSONSafely(extracted);
        if (parsed && typeof parsed === 'object' && parsed !== null) {
          logger.logParsePlatformContentParsedKeys(Object.keys(parsed));
          
          if (platformKey) {
            // Find the matching platform key (case-insensitive)
            const matchingPlatformKey = Object.keys(parsed).find(key => 
              key.toLowerCase() === platformKey.toLowerCase()
            );
            
            if (matchingPlatformKey) {
              const platformData = parsed[matchingPlatformKey];
              if (platformData && typeof platformData === 'object' && platformData !== null) {
                logger.logParsePlatformContentExtractedPlatform();
                // Extract ALL variations from the platform data (variation_1 AND variation_2)
                Object.keys(platformData).forEach((key) => {
                  if (key.startsWith('variation_')) {
                    const index = parseInt(key.replace('variation_', '')) - 1;
                    if (!isNaN(index) && platformData[key]) {
                      result.variations.push({ key, data: platformData[key], index });
                    }
                  }
                });
                logger.logParsePlatformContentVariationsFound(result.variations.length);
              } else {
                logger.logParsePlatformContentPlatformDataNotObject(typeof platformData);
              }
            } else {
              logger.logParsePlatformContentKeyNotFound(platformKey, Object.keys(parsed));
            }
          } else {
            logger.logParsePlatformContentNoPlatformKey();
          }
        } else {
          logger.logParsePlatformContentParseFailed();
        }
      } else {
        logger.logParsePlatformContentExtractFailed(contentToParse.substring(0, 500));
        
        // Fallback: Try to parse the entire content string directly (might have text before/after JSON)
        // Look for JSON structure more flexibly
        const jsonStart = contentToParse.indexOf('{');
        if (jsonStart !== -1) {
          // Try to find the end of the JSON by looking for the last } before any markdown or text sections
          // This handles cases where there's text after the JSON
          let jsonEnd = -1;
          let depth = 0;
          let inString = false;
          let escapeNext = false;
          
          for (let i = jsonStart; i < contentToParse.length; i++) {
            const char = contentToParse[i];
            
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            
            if (char === '\\') {
              escapeNext = true;
              continue;
            }
            
            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }
            
            if (!inString) {
              if (char === '{') depth++;
              else if (char === '}') {
                depth--;
                if (depth === 0) {
                  jsonEnd = i;
                  // Check if there's likely more JSON after (like another {) or if we've hit text
                  const nextChar = contentToParse.substring(i + 1, i + 10).trim();
                  if (!nextChar.startsWith('{') && !nextChar.startsWith('[')) {
                    break; // Likely end of JSON, text follows
                  }
                }
              }
            }
          }
          
          if (jsonEnd > jsonStart) {
            const jsonStr = contentToParse.substring(jsonStart, jsonEnd + 1);
            logger.logParsePlatformContentFallbackExtracted(jsonStr.length);
            const parsed = parseJSONSafely(jsonStr);
            if (parsed && typeof parsed === 'object' && parsed !== null && platformKey) {
              const matchingPlatformKey = Object.keys(parsed).find(key => 
                key.toLowerCase() === platformKey.toLowerCase()
              );
              if (matchingPlatformKey) {
                const platformData = parsed[matchingPlatformKey];
                if (platformData && typeof platformData === 'object' && platformData !== null) {
                  logger.logParsePlatformContentFallbackExtractedPlatform();
                  Object.keys(platformData).forEach((key) => {
                    if (key.startsWith('variation_')) {
                      const index = parseInt(key.replace('variation_', '')) - 1;
                      if (!isNaN(index) && platformData[key]) {
                        result.variations.push({ key, data: platformData[key], index });
                      }
                    }
                  });
                  logger.logParsePlatformContentFallbackVariationsFound(result.variations.length);
                }
              }
            }
          }
        }
      }
    }
    
    // If we didn't extract from nested JSON, use top-level variations
    if (result.variations.length === 0 && topLevelVariations.length > 0) {
      logger.logParsePlatformContentTopLevelVariations(topLevelVariations.length, topLevelVariations);
      topLevelVariations.forEach((key) => {
        const index = parseInt(key.replace('variation_', '')) - 1;
        if (!isNaN(index) && platformContent[key]) {
          result.variations.push({ key, data: platformContent[key], index });
        }
      });
    }
    
    // SPECIAL CASE: If no variations found at top level, check if variation_1.content is a JSON string
    // This handles the case where backend returns nested JSON structure
    if (result.variations.length === 0 && 
        'variation_1' in platformContent && 
        platformContent.variation_1 && 
        typeof platformContent.variation_1 === 'object' &&
        'content' in platformContent.variation_1 &&
        typeof platformContent.variation_1.content === 'string' &&
        platformContent.variation_1.content.trim().startsWith('{')) {
      
      logger.logParsePlatformContentNoTopLevelVariations();
      
      // Parse the JSON string in variation_1.content
      const extracted = extractJSONObject(platformContent.variation_1.content);
      if (extracted) {
        const parsed = parseJSONSafely(extracted);
        if (parsed && typeof parsed === 'object' && parsed !== null && platformKey) {
          // Find the matching platform key (case-insensitive)
          const matchingPlatformKey = Object.keys(parsed).find(key => 
            key.toLowerCase() === platformKey.toLowerCase()
          );
          
          if (matchingPlatformKey) {
            const platformData = parsed[matchingPlatformKey];
            if (platformData && typeof platformData === 'object' && platformData !== null) {
              logger.logParsePlatformContentExtractedPlatform();
              // Extract all variations from the platform data
              Object.keys(platformData).forEach((key) => {
                if (key.startsWith('variation_')) {
                  const index = parseInt(key.replace('variation_', '')) - 1;
                  if (!isNaN(index) && platformData[key]) {
                    result.variations.push({ key, data: platformData[key], index });
                  }
                }
              });
              logger.logParsePlatformContentVariationsFound(result.variations.length);
            }
          }
        }
      }
    }
    
    // If no variations found, use content key or whole object
    if (result.variations.length === 0) {
      if (platformContent.content) {
        result.variations.push({ key: 'content', data: platformContent, index: 0 });
      } else {
        result.variations.push({ key: 'content', data: platformContent, index: 0 });
      }
    }
  } else if (typeof platformContent === 'string') {
    result.variations.push({ key: 'content', data: platformContent, index: 0 });
  }
  
  result.variations.sort((a, b) => a.index - b.index);
  
  logger.logParsePlatformContentEnd(result.variations.length);
  return result;
};

/**
 * Strip markdown code block markers from a string
 */
const stripMarkdownCodeBlock = (str: string): string => {
  // Remove ```json\n or ```\n at the start
  let cleaned = str.trim();
  if (cleaned.startsWith('```')) {
    // Find the first newline after ```
    const firstNewline = cleaned.indexOf('\n');
    if (firstNewline !== -1) {
      cleaned = cleaned.substring(firstNewline + 1);
    } else {
      // No newline, just remove ```
      cleaned = cleaned.substring(3);
    }
  }
  
  // Remove ``` at the end
  if (cleaned.endsWith('```')) {
    cleaned = cleaned.substring(0, cleaned.length - 3).trim();
  }
  
  return cleaned.trim();
};

/**
 * Parse variation content that might be a JSON string
 */
export const parseVariationContent = (
  variationContent: string,
  platformKey: string,
  variationKey: string
): { content: string; metadata: any | null } | null => {
  logger.logParseVariationContentStart(platformKey, variationKey, variationContent.length);
  logger.logParseVariationContentPreview(variationContent.substring(0, 500));
  
  if (typeof variationContent !== 'string') {
    return null;
  }
  
  // Strip markdown code blocks if present
  let cleanedContent = variationContent;
  if (cleanedContent.includes('```')) {
    logger.logParseVariationContentStrippingMarkdown();
    cleanedContent = stripMarkdownCodeBlock(cleanedContent);
    logger.logParseVariationContentAfterStripping(cleanedContent.substring(0, 500));
  }
  
  // Extract JSON object (handles text before/after JSON)
  const extracted = extractJSONObject(cleanedContent);
  if (!extracted) {
    logger.logParseVariationContentNoJSON();
    return null;
  }
  
  const parsed = parseJSONSafely(extracted);
  if (!parsed) {
    logger.logParseVariationContentParseFailed();
    return null;
  }
  
  logger.logParseVariationContentParsedJSON(parsed);
  
  // Check if it has platform keys
  const platformKeys = ['facebook', 'linkedin', 'instagram', 'twitter', 'youtube', 'pinterest', 'email', 'blog', 'tiktok'];
  const hasPlatformKeys = Object.keys(parsed).some(key => platformKeys.includes(key.toLowerCase()));
  
  if (hasPlatformKeys && parsed[platformKey] && parsed[platformKey][variationKey]) {
    const specificVariation = parsed[platformKey][variationKey];
    if (typeof specificVariation === 'object' && 'content' in specificVariation) {
      logger.logParseVariationContentExtractedFromPlatform();
      return {
        content: typeof specificVariation.content === 'string' ? specificVariation.content : String(specificVariation.content || ''),
        metadata: specificVariation.metadata || null,
      };
    }
  } else if (typeof parsed === 'object' && parsed !== null && 'content' in parsed) {
    logger.logParseVariationContentExtractedFromDirect();
    return {
      content: typeof parsed.content === 'string' ? parsed.content : String(parsed.content || ''),
      metadata: parsed.metadata || null,
    };
  }
  
  logger.logParseVariationContentNoMatch();
  return null;
};
